import { z } from "zod"

import type { EntitlementBlockReason } from "../../billing.js"

// Pinned to the domain union so a reason renamed there is a compile error here
// rather than a row that fails to parse in the browser.
const entitlementBlockReasons = [
  "trial_expired",
  "quota_exceeded",
  "subscription_required",
] as const satisfies readonly EntitlementBlockReason[]

/** Why an attachment on a forwarded message was not imported. Defined here so
 * the ingest pipeline, the decision log's bounded detail, and the API response
 * all name the same closed set: the one thing that column must never be able to
 * hold is a sender-written string such as a filename. */
export const ApiInboxSkippedReasonSchema = z.enum([
  "embedded_image",
  "unsupported_type",
  "too_large",
  "message_limit",
  "download_failed",
  "ingest_failed",
  "duplicate",
])

export type ApiInboxSkippedReason = z.infer<typeof ApiInboxSkippedReasonSchema>

/** A sender one inbox refuses. `address` matches one From address exactly;
 * `domain` matches that domain and every subdomain under it. Stored lower
 * case. */
export const ApiInboxBlockedSenderKindSchema = z.enum(["address", "domain"])

export type ApiInboxBlockedSenderKind = z.infer<
  typeof ApiInboxBlockedSenderKindSchema
>

export const ApiInboxBlockedSenderSchema = z.object({
  blockId: z.string().uuid(),
  kind: ApiInboxBlockedSenderKindSchema,
  value: z.string(),
  createdAt: z.string().datetime(),
})

export type ApiInboxBlockedSender = z.infer<typeof ApiInboxBlockedSenderSchema>

/** Gmail's forwarding confirmation, caught on its way to a client address so
 * whoever set up the Gmail rule can finish it. Only the latest one from the
 * last 7 days is returned. */
export const ApiInboxGmailConfirmationSchema = z.object({
  /** The Gmail account that asked to forward to this address. */
  gmailAddress: z.string(),
  code: z.string(),
  /** Always https on mail-settings.google.com or mail.google.com. Null when
   * the message carried only the code. */
  confirmUrl: z.string().nullable(),
  receivedAt: z.string().datetime(),
})

export type ApiInboxGmailConfirmation = z.infer<
  typeof ApiInboxGmailConfirmationSchema
>

export const ApiInboxAddressSchema = z.object({
  inboxAddressId: z.string().uuid(),
  organizationId: z.string().uuid(),
  workspaceId: z.string().uuid(),
  /** Local-part only: a readable base, a dot, and the random token. */
  handle: z.string(),
  /** The full forward-to address users copy, handle@domain. */
  address: z.string(),
  status: z.enum(["active", "disabled"]),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  /** Senders this address refuses. Any other sender is accepted. Defaulted so
   * a web build newer than the API still parses. */
  blockedSenders: z.array(ApiInboxBlockedSenderSchema).default([]),
  /** The latest Gmail forwarding confirmation from the last 7 days. */
  gmailConfirmation: ApiInboxGmailConfirmationSchema.nullable().default(null),
  /** False when this environment cannot actually receive forwarded mail, so
   * the page can say so instead of promising an address that goes nowhere.
   *
   * Defaulted rather than required so a web build newer than the API still
   * parses. It defaults to false, not true: the whole point of this field is
   * that a page which looks live while forwarding is turned off is the bug,
   * and defaulting to true recreates exactly that during a deploy skew. */
  configured: z.boolean().default(false),
})

export type ApiInboxAddress = z.infer<typeof ApiInboxAddressSchema>

export const ApiInboxResponseSchema = z.object({
  inbox: ApiInboxAddressSchema,
})

export type ApiInboxResponse = z.infer<typeof ApiInboxResponseSchema>

export const ApiBlockInboxSenderBodySchema = z.object({
  kind: ApiInboxBlockedSenderKindSchema,
  /** An email address for `address`, a bare domain for `domain`. */
  value: z.string().trim().min(3).max(320),
})

export type ApiBlockInboxSenderBody = z.infer<
  typeof ApiBlockInboxSenderBodySchema
>

export const ApiInboxBlockedSenderListResponseSchema = z.object({
  blockedSenders: z.array(ApiInboxBlockedSenderSchema),
})

export type ApiInboxBlockedSenderListResponse = z.infer<
  typeof ApiInboxBlockedSenderListResponseSchema
>

export const ApiInboxBlockedSenderResponseSchema = z.object({
  blockedSender: ApiInboxBlockedSenderSchema,
})

export type ApiInboxBlockedSenderResponse = z.infer<
  typeof ApiInboxBlockedSenderResponseSchema
>

export const ApiUnblockInboxSenderResponseSchema = z.object({
  blockId: z.string().uuid(),
  removed: z.literal(true),
})

export type ApiUnblockInboxSenderResponse = z.infer<
  typeof ApiUnblockInboxSenderResponseSchema
>

/** What we decided about one inbound message. One value per return path the
 * inbound handler can reach with an inbox in hand, so the mapping is mechanical
 * and can be checked against the handler line by line. */
export const ApiInboxMailOutcomeSchema = z.enum([
  "imported",
  "nothing_imported",
  // The next three are history only: the sender allowlist and the SPF/DKIM
  // gate are gone, so no new row gets them. Kept so old rows still parse.
  "unverified_sender",
  "authentication_failed",
  "authentication_unattributed",
  "over_limit",
  "not_entitled",
  "inbox_inactive",
  "ingest_failed",
  "blocked_sender",
  "gmail_confirmation",
])

export type ApiInboxMailOutcome = z.infer<typeof ApiInboxMailOutcomeSchema>

/**
 * One forwarded message and what happened to it. Deliberately carries nothing
 * the sender wrote as content: no subject, no body, no filenames, no headers,
 * and not the provider's message id either.
 */
export const ApiInboxMailEventSchema = z.object({
  eventId: z.string().uuid(),
  outcome: ApiInboxMailOutcomeSchema,
  /** The From address as the message claims it. Null for mail to a paused
   * address, which keeps the domain only, and when the From did not parse. */
  senderAddress: z.string().nullable(),
  /** Null only when the From could not be parsed at all. */
  senderDomain: z.string().nullable(),
  /** True when SPF, DKIM or DMARC passed for the From domain. A signal, not a
   * gate: false means the address above is a claim, not a confirmed
   * identity, and an auto-forwarded bill is often false. */
  senderAuthenticated: z.boolean(),
  importedCount: z.number().int(),
  skippedCount: z.number().int(),
  /** Counts of why attachments were not imported, keyed by the same closed set
   * the ingest pipeline and the log column use. Partial: a message usually has
   * no skips at all. */
  skippedReasons: z
    .partialRecord(ApiInboxSkippedReasonSchema, z.number())
    .optional(),
  blockReason: z.enum(entitlementBlockReasons).nullable(),
  /** Present on an unattributed verdict: false means forwarding was not fully
   * turned on when the mail arrived. */
  authservIdConfigured: z.boolean().optional(),
  receivedAt: z.string().datetime(),
})

export type ApiInboxMailEvent = z.infer<typeof ApiInboxMailEventSchema>

export const ApiInboxMailEventListResponseSchema = z.object({
  events: z.array(ApiInboxMailEventSchema),
})

export type ApiInboxMailEventListResponse = z.infer<
  typeof ApiInboxMailEventListResponseSchema
>
